import { observer } from 'mobx-react-lite'
import { EditPencil, Notes, Trash } from 'iconoir-react'
import { useMemo } from 'react'
import { ColumnDef } from '@tanstack/react-table'
import moment from 'moment'
import { Button } from 'app/components/ui/button'
import { PageScroll } from 'app/components/layout/PageScroll'
import { PageHeader } from 'app/components/ui/page-header'
import { TooltipWrapper } from 'app/components/ui/tooltip-wrapper'
import SimpleTable from 'app/components/table/SimpleTable'
import { TableEmptyState } from 'app/components/ui/table-empty-state'
import { useTemplateTabs } from 'app/components/templates/useTemplateTabs'
import type { NoteTemplate } from 'app/generated/graphql'
import { NoteTemplateEditorDialog } from './NoteTemplateEditorDialog'
import type { UseNoteTemplatesReturn } from './useNoteTemplates'

export const NoteTemplatesShadcnComponent = observer(function NoteTemplatesShadcnComponent({
  templates,
  saving,
  setSearch,
  editing,
  openNew,
  openEdit,
  closeEditor,
  saveTemplate,
  removeTemplate,
}: UseNoteTemplatesReturn) {
  const { tabs } = useTemplateTabs()
  const columns = useMemo<ColumnDef<NoteTemplate>[]>(
    () => [
      {
        id: `name`,
        accessorKey: `name`,
        header: `Name`,
        cell: (props) => <span className="font-medium">{props.getValue<string>()}</span>,
        meta: { defaultVisible: true },
      },
      {
        id: `body`,
        accessorKey: `body`,
        header: `Note`,
        cell: (props) => (
          <span className="line-clamp-2 max-w-xl whitespace-pre-line text-subtle">{props.getValue<string>()}</span>
        ),
        meta: { defaultVisible: true },
      },
      {
        id: `updated`,
        accessorKey: `updatedDate`,
        header: `Updated`,
        cell: (props) => moment(props.getValue<string>()).format(`MMM D, YYYY`),
        meta: { defaultVisible: true },
      },
      {
        id: `actions`,
        header: ``,
        meta: { defaultVisible: true },
        cell: (props) => (
          <div className="flex items-center gap-3">
            <TooltipWrapper message="Edit template">
              <EditPencil
                className="h-4 w-4 cursor-pointer text-subtle hover:text-default"
                onClick={(e) => {
                  e.stopPropagation()
                  openEdit(props.row.original)
                }}
              />
            </TooltipWrapper>
            <TooltipWrapper message="Delete template">
              <Trash
                className="h-4 w-4 cursor-pointer text-red-500 hover:text-red-700"
                onClick={(e) => {
                  e.stopPropagation()
                  removeTemplate(props.row.original)
                }}
              />
            </TooltipWrapper>
          </div>
        ),
      },
    ],
    [openEdit, removeTemplate],
  )

  return (
    <>
      <PageScroll variant="table" header={<PageHeader title="Templates" tabs={tabs} />}>
        <div className="h-full pt-6">
          <SimpleTable
            columns={columns}
            data={templates}
            hideFooter
            meta={{ onRowClick: openEdit }}
            emptyState={
              <TableEmptyState
                icon={<Notes className="h-6 w-6" strokeWidth={1.5} />}
                title="No note templates yet"
                description="Turn the sheets your front desk fills in on every call into a note that is already laid out, like a new patient call sheet. Pick one from Add note on any contact and just fill in the answers."
                action={
                  <Button variant="primary" size="sm" onClick={openNew}>
                    New Template
                  </Button>
                }
              />
            }
            edgePadded
            onSearchValueChange={setSearch}
            extraSlot={
              <Button variant="primary" size="sm" onClick={openNew}>
                New Template
              </Button>
            }
          />
        </div>
      </PageScroll>
      <NoteTemplateEditorDialog
        open={!!editing}
        template={editing === `new` ? undefined : editing}
        saving={saving}
        onSave={saveTemplate}
        onCancel={closeEditor}
      />
    </>
  )
})
